import React, { useCallback, useMemo } from 'react';
import isNil from 'lodash/isNil';
import styled from 'styled-components';
import { IgnoredChecksBreakdown, SplitChecksToggleBreakdown, ToggleableBreakdown } from 'components';
import EmployeePayoutAccordion from 'components/Calculator/EmployeePayoutAccordion/EmployeePayoutAccordion';
import { ParticipantListItem } from 'components/Calculator/ParticipantListItem';
import { theme, toRem } from 'design';
import { DisbursementMethod, ICalculatorRules, IPaymentSet, ITipCalculatorPayee, ITipCalculatorResultWarning, ITipCalculatorResultWarningUnallocatedRemainder } from 'types';
import { buildEmployeeOverrideTicketBreakdown, buildGroupedTipCalculatorPayeesFromPayouts, buildTipCalculatorParticipants, buildTipCalculatorWarnings } from 'utils';
import { sortAlphaNumerically } from 'utils/arrays';
import { IExportData } from './types';

interface ICalculationsReportProps {
  header: (data?: IExportData, capWarnings?: ITipCalculatorResultWarning[], unallocatedFundsWarnings?: ITipCalculatorResultWarningUnallocatedRemainder[]) => React.ReactNode;
  paymentSet: IPaymentSet;
  isLoading?: boolean;
}

const StyledToggleableBreakdown = styled(ToggleableBreakdown)`
  margin-bottom: ${toRem(theme.space.lg)};
`;

const NoteSection = styled.div`
  width: 100%;
  border-top: 1px solid ${theme.color.neutral[200]};
  padding: ${toRem(theme.space.lg)} 0;
  margin-top: ${toRem(theme.space.lg)};
`;

const Note = styled.div`
  background-color: ${theme.color.white};
  border: 1px solid ${theme.color.neutral[200]};
  border-radius: ${toRem(theme.borderRadius.xsmall)};
  font-size: ${toRem(theme.font.size.md)};
  padding: ${toRem(theme.space.md)} ${toRem(theme.space.md)};
  width: 100%;
`;

const OverrideChecksWrapper = styled.div`
  &:not(:first-child) {
    margin-bottom: ${toRem(theme.space.lg)};
  }
  width: 100%;
`;

const CalculationsReport = ({ header, paymentSet, isLoading }: ICalculationsReportProps) => {
  const { calculations, payouts } = paymentSet || {};

  const { cappedWarnings, unallocatedFundsWarnings } = useMemo(() => buildTipCalculatorWarnings(calculations?.results || []), [calculations?.results]);

  const renderBreakdownForPayee = useCallback(
    (payeeId: string) =>
      calculations?.results.map((segment) => {
        const employeePayouts = segment.employees.filter((employee) => employee.id === payeeId && (employee.received.length || employee.contributions.length || employee.retained?.total > 0));
        if (!payeeId || !employeePayouts?.length) return <></>;

        return employeePayouts
          .filter((r) => r.calculated.total > 0 || r.totals.totalMinWorked > 0)
          .map((employeePayout, idx) => (
            <StyledToggleableBreakdown
              key={`segment-breakdown-${payeeId}-${idx}`}
              categoryGroups={segment.meta.categoryGroups}
              calculatorRules={segment.rules}
              calculation={employeePayout}
              segmentName={segment.segment}
              creditCardFee={segment.creditCardPercentFee}
            />
          ));
      }),
    [calculations]
  );

  const renderOverrideBreakdown = useCallback(
    (payeeId: string) => {
      const employeePayouts = calculations?.results.flatMap((result) => result.employees.filter((employee) => employee.id === payeeId));
      const { splitChecks, ignoredChecks } = buildEmployeeOverrideTicketBreakdown(employeePayouts);

      return (
        <>
          {(!!splitChecks.received?.length || !!splitChecks.contributions?.length) && (
            <OverrideChecksWrapper>
              <SplitChecksToggleBreakdown splitChecks={splitChecks} />
            </OverrideChecksWrapper>
          )}
          {!!ignoredChecks.length && (
            <OverrideChecksWrapper>
              <IgnoredChecksBreakdown ignoredCheckData={ignoredChecks.sort(sortAlphaNumerically('displayId'))} />
            </OverrideChecksWrapper>
          )}
        </>
      );
    },
    [calculations]
  );

  const includedPayouts = useMemo(
    () => ({
      cash: payouts.some((payout) => payout.disbursementMethod === DisbursementMethod.CASH),
      holdbacks: payouts.some((payout) => !isNil(payout.holdback)),
      instant: payouts.some((payout) => payout.disbursementMethod === DisbursementMethod.INSTANT),
      payroll: payouts.some((payout) => payout.disbursementMethod === DisbursementMethod.PAYROLL),
    }),
    [payouts]
  );

  const renderParticipants = () => {
    if (isLoading || !calculations?.results[0].participants?.length) {
      return <></>;
    }

    const participants = buildTipCalculatorParticipants(calculations.results);

    return participants.map((participant, index) => (
      <ParticipantListItem key={`participant-${index}`} participant={participant} calculatorRules={{ categoryGroups: calculations.results[index].meta.categoryGroups, rules: calculations.results[index].rules } as ICalculatorRules} />
    ));
  };

  // Group payouts by employee ID to create a consolidated list of payees
  const groupedPayees: ITipCalculatorPayee[] = useMemo(() => buildGroupedTipCalculatorPayeesFromPayouts(paymentSet), [paymentSet]);

  return (
    <>
      {header(undefined, cappedWarnings, unallocatedFundsWarnings)}
      {isLoading || !groupedPayees?.length ? (
        <EmployeePayoutAccordion key="loading-payouts" isLoading includedPayouts={{ cash: false, holdbacks: false, instant: true, payroll: false }} />
      ) : (
        <>
          {renderParticipants()}
          {groupedPayees?.map((payee, idx) => (
            <EmployeePayoutAccordion key={`payee-${payee.id}-${idx}`} dataCy="payout-calculations-breakdown" payee={payee} includedPayouts={includedPayouts}>
              {renderBreakdownForPayee(payee.id)}
              {renderOverrideBreakdown(payee.id)}
              {payee.note && (
                <NoteSection>
                  <Note>{payee.note}</Note>
                </NoteSection>
              )}
            </EmployeePayoutAccordion>
          ))}
        </>
      )}
    </>
  );
};

export default CalculationsReport;
